import { useCallback, useEffect, useRef, useState } from 'react';

export type SlideDirection = 'up' | 'down';

const SLIDE_STEP_MS = 70;
export const SLIDE_FADE_MS = 140;

export const useReviewTransition = () => {
  const [showContent, setShowContent] = useState(true);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('up');
  const generation = useRef(0);
  const timers = useRef(new Set<number>());

  const wait = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
          timers.current.delete(timer);
          resolve();
        }, ms);
        timers.current.add(timer);
      }),
    []
  );

  const cancel = useCallback(() => {
    generation.current += 1;
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current.clear();
  }, []);

  useEffect(() => cancel, [cancel]);

  const slide = useCallback(
    async (direction: SlideDirection, apply: () => void | Promise<void>) => {
      cancel();
      const current = generation.current;
      setSlideDirection(direction);
      setShowContent(false);
      await wait(SLIDE_STEP_MS);
      if (current !== generation.current) return;
      await apply();
      if (current !== generation.current) return;
      await wait(SLIDE_STEP_MS);
      if (current !== generation.current) return;
      setShowContent(true);
    },
    [cancel, wait]
  );

  return { showContent, setShowContent, slideDirection, setSlideDirection, slide, cancel };
};
